import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Dialog,
  IconButton,
  Typography,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import TextField from "../../../../Component/Forms/TextField";
import SearchableSelect from "../../../../Component/Forms/SearchableSelect";

import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";

import styles from "./styles";
import { addEntityContact, updateEntityContact } from "../../../../API/Entity";
import { getListValues } from "../../../../API/List";
import { getGeographies } from "../../../../API/Country";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../Component/CustomButton";

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const CreateAddress = (props) => {
  const { open, onclose, newContact, editContact, entity, address, action } =
    props;
  const classes = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(open);
  const [addressId, setAddressId] = useState();
  const [addressType, setAddressType] = useState(null);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState();
  const [addressLine3, setAddressLine3] = useState();
  const [addressLine4, setAddressLine4] = useState();
  const [town, setTown] = useState();
  const [county, setCounty] = useState();
  const [postCode, setPostCode] = useState();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [region, setRegion] = useState(null);
  const [addressTypeErrorMessage, setAddressTypeErrorMessage] = useState();
  const [addressErrorMessage, setAddressErrorMessage] = useState();
  const [townErrorMessage, setTownErrorMessage] = useState();
  const [countryErrorMessage, setCountryErrorMessage] = useState();
  const [postCodeErrorMessage, setPostCodeErrorMessage] = useState();
  const [regionErrorMessage, setRegionErrorMessage] = useState();
  const [countryList, setCountryList] = useState([]);
  const [addressTypeList, setAddressTypeList] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [isNewContact, setNewContact] = useState(false);
  const [isEditContact, setIsEditContact] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const { showErrorMessage, showMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  const { id: entityId } = entity || {};

  useEffect(() => {
    const load = async () => {
      try {
        setIsDialogOpen(true);
        setIsLoading(true);
        setNewContact(newContact);
        setIsEditContact(editContact);
        const {
          id,
          address_type,
          address_line_1,
          address_line_2,
          address_line_3,
          address_line_4,
          city,
          county,
          country_id,
          jll_region,
          postcode,
        } = address || {};

        setAddressId(id);
        setAddressLine1(address_line_1);
        setAddressLine2(address_line_2);
        setAddressLine3(address_line_3);
        setAddressLine4(address_line_4);
        //setRegion(jll_region);
        setPostCode(postcode);
        setTown(city);
        setCounty(county);

        const resuts = await getListValues({ listType: "CL" });
        let { data = [] } = resuts;
        setCountryList(
          data.map((c) => {
            if (c.code === country_id) {
              setSelectedCountry({
                value: c.code,
                label: c.value,
                region: c.region,
              });
            }
            return { value: c.code, label: c.value, region: c.region };
          }),
        );

        const listResult = await getListValues({ listId: "4" });
        setAddressTypeList(
          listResult.data.map((l) => {
            if (
              address_type &&
              l.code.toLowerCase() === address_type.toLowerCase()
            ) {
              setAddressType({ value: l.code, label: l.value });
            }
            return { value: l.code, label: l.value };
          }),
        );

        const geographyData = await getGeographies();
        setRegionList(
          geographyData.data.map((g) => {
            if (
              jll_region &&
              g.name.toLowerCase() === jll_region.toLowerCase()
            ) {
              setRegion({ value: g.name, label: g.name });
            }
            return { value: g.name, label: g.name };
          }),
        );
      } catch (error) {
        showErrorMessage({ error });
      }
      setIsLoading(false);
    };
    if (open) {
      load();
    }
  }, [open]);

  const handleClose = (status) => {
    setIsDialogOpen(false);
    setIsEditContact(false);
    setNewContact(false);
    setAddressId();
    setAddressLine1();
    setAddressLine2();
    setAddressLine3();
    setAddressLine4();
    setRegion(null);
    setPostCode();
    setTown();
    setSelectedCountry(null);
    setAddressType(null);
    if (onclose) {
      onclose(status);
    }
  };

  const cancel = () => {
    handleClose("cancel");
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const validateAddress = () => {
    if (!addressType || addressType === "") {
      setAddressTypeErrorMessage(
        t("messages.record.addresses.addressTypeError"),
      );
      return false;
    }

    if (!addressLine1 || addressLine1 === "") {
      setAddressErrorMessage(t("messages.record.addresses.addressLine1Error"));
      return false;
    } else if (addressLine1.length > 500) {
      setAddressErrorMessage(
        t("messages.record.addresses.addressLine1LengthError"),
      );
      return false;
    }

    if (!town || town === "") {
      setTownErrorMessage(t("messages.record.addresses.cityError"));
      return false;
    } else if (town.length > 50) {
      setTownErrorMessage(t("messages.record.addresses.cityLengthError"));
      return false;
    }

    if (postCode && postCode.length > 10) {
      setPostCodeErrorMessage(
        t("messages.record.addresses.postcodeLengthError"),
      );
      return false;
    }

    if (!selectedCountry) {
      setCountryErrorMessage(t("messages.record.addresses.countryError"));
      return false;
    }

    if (!region || region === "") {
      setRegionErrorMessage(t("messages.record.addresses.regionError"));
      return false;
    }

    return true;
  };

  const updateContact = async () => {
    if (!validateAddress()) {
      return;
    }
    setIsLoading(true);
    try {
      const results = await updateEntityContact({
        id: addressId,
        entityId,
        addressType: addressType.value,
        addressLine1,
        addressLine2,
        addressLine3,
        addressLine4,
        town,
        county,
        region: region ? region.value : null,
        postCode,
        countryId: selectedCountry.value,
      });
      const { message } = results;
      showMessage({ message });
      handleClose();
    } catch (error) {
      showErrorMessage({ error });
    }
    handleClose();
  };

  const submitContact = async () => {
    if (!validateAddress()) {
      return;
    }
    setIsLoading(true);
    try {
      const results = await addEntityContact({
        entityId,
        addressType: addressType.value,
        addressLine1,
        addressLine2,
        addressLine3,
        addressLine4,
        town,
        county,
        region: region ? region.value : null,
        postCode,
        countryId: selectedCountry.value,
        countryName: selectedCountry.label,
        action,
      });
      const { message } = results;
      showMessage({ message });
      handleClose();
    } catch (error) {
      showErrorMessage({ error });
    }
    handleClose();
  };

  const renderCreateForm = () => {
    return (
      <Grid container direction="row" spacing={3}>
        <Grid item sm={6} md={6} xs={12}>
          <SearchableSelect
            label={t("labels.record.addresses.form.addressType")}
            options={addressTypeList}
            value={addressType}
            changeHandler={setAddressType}
            disableClearable={true}
            required={true}
          />
          <div className={classes.errorText}>{addressTypeErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.addresses.form.addressLine1")}
            name="input_add1"
            type="text"
            id="input_add1"
            placeholder={t("labels.record.addresses.form.enterAddressLine1")}
            initialValue={addressLine1}
            value={addressLine1}
            onChange={setAddressLine1}
            required={true}
          />
          <div className={classes.errorText}>{addressErrorMessage}</div>
        </Grid>

        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.addresses.form.addressLine2")}
            name="input_add2"
            type="text"
            id="input_add2"
            placeholder={t("labels.record.addresses.form.enterAddressLine2")}
            initialValue={addressLine2}
            value={addressLine2}
            onChange={setAddressLine2}
          />
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.addresses.form.addressLine3")}
            name="input_add3"
            type="text"
            id="input_add3"
            placeholder={t("labels.record.addresses.form.enterAddressLine3")}
            initialValue={addressLine3}
            value={addressLine3}
            onChange={setAddressLine3}
          />
        </Grid>

        <Grid item sm={6} md={6} xs={12}>
          <TextField
            label={t("labels.record.addresses.form.addressLine4")}
            name="input_add4"
            type="text"
            id="input_add4"
            placeholder={t("labels.record.addresses.form.enterAddressLine4")}
            initialValue={addressLine4}
            value={addressLine4}
            onChange={setAddressLine4}
          />
        </Grid>

        <Grid item sm={6} md={3} xs={12}>
          <TextField
            label={t("labels.record.addresses.form.city")}
            name="input_city"
            type="text"
            id="input_city"
            placeholder={t("labels.record.addresses.form.enterCity")}
            initialValue={town}
            value={town}
            onChange={setTown}
            required={true}
          />
          <div className={classes.errorText}>{townErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <TextField
            label={t("labels.record.addresses.form.county")}
            name="input_county"
            type="text"
            id="input_county"
            placeholder={t("labels.record.addresses.form.enterCounty")}
            initialValue={county}
            value={county}
            onChange={setCounty}
          />
        </Grid>

        <Grid item sm={6} md={3} xs={12}>
          <SearchableSelect
            label={t("labels.record.addresses.form.country")}
            options={countryList}
            value={selectedCountry}
            changeHandler={(e) => {
              if (e) {
                setRegion({ value: e.region, label: e.region });
              }
              setSelectedCountry(e);
            }}
            required={true}
          />
          <div className={classes.errorText}>{countryErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <SearchableSelect
            label={t("labels.record.addresses.form.region")}
            options={regionList}
            value={region}
            changeHandler={setRegion}
            required={true}
          />
          <div className={classes.errorText}>{regionErrorMessage}</div>
        </Grid>
        <Grid item sm={6} md={3} xs={12}>
          <TextField
            label={t("labels.record.addresses.form.postCode")}
            name="input_postCode"
            type="text"
            id="input_postCode"
            placeholder={t("labels.record.addresses.form.enterPostCode")}
            initialValue={postCode}
            value={postCode}
            onChange={setPostCode}
            required={false}
          />
          <div className={classes.errorText}>{postCodeErrorMessage}</div>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Dialog
        disableBackdropClick
        onClose={cancel}
        aria-labelledby="add-document-modal"
        open={isDialogOpen}
        fullWidth={true}
        maxWidth="lg"
      >
        <DialogTitle id="add-document-modal" onClose={cancel}>
          <Typography className={classes.panelTitle}>
            {t("labels.record.addresses.sectionHeader")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? renderLoadingIndicator() : renderCreateForm()}
        </DialogContent>

        <DialogActions>
          <CustomButton variant="secondary" onClick={cancel}>
            {t("labels.common.close")}
          </CustomButton>
          {isNewContact && (
            <CustomButton
              variant="primary"
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={submitContact}
            >
              {t("labels.common.submit")}
            </CustomButton>
          )}
          {isEditContact && (
            <CustomButton
              variant="primary"
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={updateContact}
            >
              {t("labels.common.update")}
            </CustomButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateAddress;
